import { CSSProperties, ReactNode } from 'react';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';

import classes from './style.module.css';

interface TableRowClickableProps extends React.HTMLAttributes<HTMLTableRowElement> {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  onRowClick: () => void;
  ariaLabel?: string;
}

/**
 * Clickable table row component with built-in hover effects and keyboard accessibility
 * @param onRowClick - Function to call when row is clicked
 * @param ariaLabel - Accessible label for the clickable row
 */
export const TableRowClickable: React.FC<TableRowClickableProps> = ({
  className,
  style,
  children,
  onRowClick,
  ariaLabel,
  onClick,
  onKeyDown,
  ...rest
}) => {
  const handleClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    onRowClick();
    if (onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick();
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const rowClassName = classNames(
    classes.tableRow,
    classes.tableRowClickable,
    className
  );

  return (
    <tr
      className={rowClassName}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
      {...rest}
    >
      <td className={classes.chevronCell}>
        <div className={classes.bubble}>
          <ChevronRight size={16} className={classes.bubbleIcon} />
        </div>
      </td>
      {children} 
    </tr>
  );
};
